(function() {
  'use strict';

  class MtAnimationSlideshow {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-slideshow-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-slideshow-dot]'));
      this.prevBtn = root.querySelector('[data-slideshow-prev]');
      this.nextBtn = root.querySelector('[data-slideshow-next]');
      this.viewport = root.querySelector('.mt-animation-slideshow__viewport');
      this.activeIndex = 0;
      this.destroyed = false;
      this.isTransitioning = false;

      // Touch swipe variables
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchEndX = 0;
      this.touchEndY = 0;
      this.isDragging = false;
      this.isHorizontalSwipe = false;

      if (!this.slides.length) {
        this.destroyed = true;
        return;
      }

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);

      this.init();
    }

    init() {
      this.updateSlides();
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', this.onPrev);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', this.onNext);
      }
      this.dots.forEach((dot) => {
        dot.addEventListener('click', this.onDotClick);
      });

      // Add touch swipe support for mobile
      if (this.viewport) {
        this.viewport.addEventListener('touchstart', this.handleTouchStart, { passive: false });
        this.viewport.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        this.viewport.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      }
    }

    onPrev(event) {
      event.preventDefault();
      this.goTo(this.activeIndex - 1);
    }

    onNext(event) {
      event.preventDefault();
      this.goTo(this.activeIndex + 1);
    }

    onDotClick(event) {
      event.preventDefault();
      const dot = event.currentTarget;
      const index = parseInt(dot.getAttribute('data-slideshow-dot'), 10);
      if (!Number.isNaN(index)) {
        this.goTo(index);
      }
    }

    goTo(index) {
      if (!this.slides.length || this.isTransitioning) return;

      let target = index;
      if (target < 0) {
        target = this.slides.length - 1;
      } else if (target >= this.slides.length) {
        target = 0;
      }

      if (target === this.activeIndex) {
        return;
      }

      // Set transitioning flag before preloading
      this.isTransitioning = true;

      // Preload the target slide's image before switching
      this.preloadSlideImage(target).then(() => {
        // Small delay for Android to ensure image is rendered
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.activeIndex = target;
            this.updateSlides();
            this.isTransitioning = false;
          });
        });
      }).catch(() => {
        // If preload fails, still switch (fallback)
        this.activeIndex = target;
        this.updateSlides();
        this.isTransitioning = false;
      });
    }

    preloadSlideImage(index) {
      return new Promise((resolve) => {
        const targetSlide = this.slides[index];
        if (!targetSlide) {
          resolve(undefined);
          return;
        }

        // Check if slide has an image
        const img = targetSlide.querySelector('.mt-animation-slideshow__image');
        if (!img) {
          // No image to preload, resolve immediately
          resolve(undefined);
          return;
        }

        // Force the DOM img element to start loading if it hasn't (for lazy loading)
        // This is crucial for first-time switches on mobile
        if (!img.complete || img.naturalHeight === 0) {
          // If image has loading="lazy", we need to trigger it to load
          if (img.loading === 'lazy') {
            // Force eager loading by temporarily changing the loading attribute
            img.loading = 'eager';
          }
          
          // Also ensure the image source is set (in case of picture element)
          const imageUrl = img.currentSrc || img.src;
          if (imageUrl && img.src !== imageUrl) {
            img.src = imageUrl;
          }
        }

        // Check if image is already loaded and decoded
        if (img.complete && img.naturalHeight !== 0) {
          // For Android, ensure image is decoded
          if (img.decode && typeof img.decode === 'function') {
            img.decode()
              .then(() => {
                // Additional delay for Android rendering
                setTimeout(() => resolve(undefined), 50);
              })
              .catch(() => {
                // If decode fails, still continue with small delay
                setTimeout(() => resolve(undefined), 50);
              });
          } else {
            // Fallback for browsers without decode API
            setTimeout(() => resolve(undefined), 50);
          }
          return;
        }

        // Use the img element's currentSrc which respects picture sources
        const imageUrl = img.currentSrc || img.src;
        if (!imageUrl) {
          resolve(undefined);
          return;
        }

        // Wait for the DOM img element to load (not just a preloaded Image object)
        // This ensures the actual displayed image is ready
        const timeout = setTimeout(() => {
          // Timeout after 5 seconds for Android (longer than iOS)
          resolve(undefined);
        }, 5000);

        // Check if image is already loading
        const checkImageReady = () => {
          if (img.complete && img.naturalHeight !== 0) {
            clearTimeout(timeout);
            // Image is loaded, now decode it for Android
            if (img.decode && typeof img.decode === 'function') {
              img.decode()
                .then(() => {
                  // Additional delay for Android rendering after decode
                  setTimeout(() => resolve(undefined), 100);
                })
                .catch(() => {
                  // If decode fails, still continue with delay
                  setTimeout(() => resolve(undefined), 100);
                });
            } else {
              // Fallback: wait a bit more for Android browsers
              setTimeout(() => resolve(undefined), 150);
            }
            return true;
          }
          return false;
        };

        // If image is already loaded, resolve immediately
        if (checkImageReady()) {
          return;
        }

        // Wait for the image to load
        const onImageLoad = () => {
          clearTimeout(timeout);
          img.removeEventListener('load', onImageLoad);
          img.removeEventListener('error', onImageError);
          
          // Image is loaded, now decode it for Android
          if (img.decode && typeof img.decode === 'function') {
            img.decode()
              .then(() => {
                // Additional delay for Android rendering after decode
                setTimeout(() => resolve(undefined), 100);
              })
              .catch(() => {
                // If decode fails, still continue with delay
                setTimeout(() => resolve(undefined), 100);
              });
          } else {
            // Fallback: wait a bit more for Android browsers
            setTimeout(() => resolve(undefined), 150);
          }
        };

        const onImageError = () => {
          clearTimeout(timeout);
          img.removeEventListener('load', onImageLoad);
          img.removeEventListener('error', onImageError);
          // Even if image fails to load, continue with transition
          resolve(undefined);
        };

        // Add event listeners
        img.addEventListener('load', onImageLoad, { once: true });
        img.addEventListener('error', onImageError, { once: true });

        // Also preload in background for better performance
        const imageLoader = new Image();
        imageLoader.src = imageUrl;
      });
    }

    // Background preloading (non-blocking) for smoother Android transitions
    preloadSlideImageBackground(index) {
      const targetSlide = this.slides[index];
      if (!targetSlide) return;

      const img = targetSlide.querySelector('.mt-animation-slideshow__image');
      if (!img) return;

      // Skip if already loaded
      if (img.complete && img.naturalHeight !== 0) return;

      const imageUrl = img.currentSrc || img.src;
      if (!imageUrl) return;

      // Preload in background without blocking
      const imageLoader = new Image();
      imageLoader.src = imageUrl;
    }

    handleTouchStart(e) {
      if (window.innerWidth > 749 || this.isTransitioning) return; // Only on mobile
      const touch = e.touches[0];
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.isDragging = true;
      this.isHorizontalSwipe = false;
      
      // Preload adjacent slides in the background for smoother transitions on Android
      // Preload next slide
      const nextIndex = (this.activeIndex + 1) % this.slides.length;
      const prevIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
      
      // Start preloading adjacent slides (non-blocking)
      this.preloadSlideImageBackground(nextIndex);
      this.preloadSlideImageBackground(prevIndex);
    }

    handleTouchMove(e) {
      if (!this.isDragging || window.innerWidth > 749 || this.isTransitioning) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;

      // Determine swipe direction after a minimum movement
      if (!this.isHorizontalSwipe && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
        this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
        
        // Once we know it's a horizontal swipe, start preloading target image immediately
        if (this.isHorizontalSwipe) {
          let targetIndex;
          if (deltaX > 0) {
            // Swiping right - will go to previous
            targetIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
          } else {
            // Swiping left - will go to next
            targetIndex = (this.activeIndex + 1) % this.slides.length;
          }
          // Start preloading target image in background (non-blocking)
          this.preloadSlideImageBackground(targetIndex);
        }
      }

      // Only prevent default for horizontal swipes
      if (this.isHorizontalSwipe) {
        e.preventDefault();
      }
    }

    handleTouchEnd(e) {
      if (!this.isDragging || window.innerWidth > 749 || this.isTransitioning) return;
      this.isDragging = false;

      const touch = e.changedTouches[0];
      this.touchEndX = touch.clientX;
      this.touchEndY = touch.clientY;

      const deltaX = this.touchEndX - this.touchStartX;
      const deltaY = this.touchEndY - this.touchStartY;
      const minSwipeDistance = 50; // Minimum distance to trigger swipe

      // Only handle horizontal swipes for navigation
      if (this.isHorizontalSwipe && Math.abs(deltaX) > minSwipeDistance) {
        // Determine target index
        let targetIndex;
        if (deltaX > 0) {
          // Swipe right - go to previous
          targetIndex = (this.activeIndex - 1 + this.slides.length) % this.slides.length;
        } else {
          // Swipe left - go to next
          targetIndex = (this.activeIndex + 1) % this.slides.length;
        }
        
        // Preload target image before switching (especially important for Android)
        this.preloadSlideImage(targetIndex).then(() => {
          if (deltaX > 0) {
            this.onPrev(e);
          } else {
            this.onNext(e);
          }
        }).catch(() => {
          // If preload fails, still switch
          if (deltaX > 0) {
            this.onPrev(e);
          } else {
            this.onNext(e);
          }
        });
      }

      this.isHorizontalSwipe = false;
    }

    updateSlides() {
      // First, add is-active to the new slide (before removing from old)
      // This prevents a brief moment where no slide is visible
      this.slides.forEach((slide, idx) => {
        const isActive = idx === this.activeIndex;
        if (isActive) {
          // Add is-active to new slide first
          slide.classList.add('is-active');
          controlSlideshowMedia(slide, true);
        }
      });

      // Then remove is-active from old slides
      this.slides.forEach((slide, idx) => {
        const isActive = idx === this.activeIndex;
        if (!isActive) {
          slide.classList.remove('is-active');
          controlSlideshowMedia(slide, false);
        }
      });

      this.dots.forEach((dot, idx) => {
        const isActive = idx === this.activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    destroy() {
      if (this.destroyed) return;
      if (this.prevBtn) {
        this.prevBtn.removeEventListener('click', this.onPrev);
      }
      if (this.nextBtn) {
        this.nextBtn.removeEventListener('click', this.onNext);
      }
      this.dots.forEach((dot) => {
        dot.removeEventListener('click', this.onDotClick);
      });
      if (this.viewport) {
        this.viewport.removeEventListener('touchstart', this.handleTouchStart);
        this.viewport.removeEventListener('touchmove', this.handleTouchMove);
        this.viewport.removeEventListener('touchend', this.handleTouchEnd);
      }
      this.slides.forEach((slide) => {
        controlSlideshowMedia(slide, false);
      });
      this.destroyed = true;
    }
  }

  function controlSlideshowMedia(slide, shouldPlay) {
    if (!slide) return;
    const video = slide.querySelector('[data-slideshow-video]');
    if (!video) return;

    if (shouldPlay) {
      if (video.readyState >= 2) {
        video.play().catch(() => {});
      } else {
        const onCanPlay = () => {
          video.play().catch(() => {});
          video.removeEventListener('canplay', onCanPlay);
        };
        video.addEventListener('canplay', onCanPlay);
      }
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }

  const instances = new Map();

  function initSlideshows(scope = document) {
    const roots = scope.querySelectorAll('[data-animation-slideshow]');
    roots.forEach((root) => {
      if (instances.has(root)) return;
      const instance = new MtAnimationSlideshow(root);
      if (!instance.destroyed) {
        instances.set(root, instance);
      }
    });
  }

  function destroySlideshows(scope = document) {
    const roots = scope.querySelectorAll('[data-animation-slideshow]');
    roots.forEach((root) => {
      const instance = instances.get(root);
      if (instance) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSlideshows());
  } else {
    initSlideshows();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initSlideshows(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySlideshows(event.target);
  });
})();

